
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { UserRole, RequestStatus, MeetingPlatform, ChatRequest, SchedulingStatus } from '../types.ts';
import { dbService } from '../services/dbService.ts';
import { aiService } from '../services/aiService.ts';

const ChatView = ({ user, activeTab, selectedId }: any) => {
  const isPro = user.role === UserRole.PROFESSIONAL;
  const [requests, setRequests] = useState<ChatRequest[]>(dbService.getRequestsForUser(user.id, user.role));
  const [selectedReq, setSelectedReq] = useState<ChatRequest | null>(null);
  const [msgInput, setMsgInput] = useState("");
  const [showScheduling, setShowScheduling] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Advanced Scheduling State
  const [selectedDate, setSelectedDate] = useState("");
  const [finalSlots, setFinalSlots] = useState<string[]>([]);
  
  // Sage (AI Guide) State for Students
  const [aiCoachingResult, setAiCoachingResult] = useState<any>(null);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [currentPromptType, setCurrentPromptType] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updatedRequests = dbService.getRequestsForUser(user.id, user.role);
    setRequests(updatedRequests);
    
    // Auto-select if a specific ID was passed from dashboard
    if (selectedId) {
      const target = updatedRequests.find(r => r.id === selectedId);
      if (target) setSelectedReq(target);
    }
  }, [user.id, user.role, activeTab, selectedId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [selectedReq?.messages]);

  const handleSendMsg = () => {
    if (!msgInput.trim() || !selectedReq) return;
    const updated = dbService.addMessageToRequest(selectedReq.id, { senderId: user.id, text: msgInput });
    if (updated) {
      setSelectedReq(updated);
      setRequests(dbService.getRequestsForUser(user.id, user.role));
      setMsgInput("");
      setAiCoachingResult(null);
      setCurrentPromptType(null);
    }
  };

  const handleConfirmTime = async (time: string) => {
    if (!selectedReq) return;
    const platform = selectedReq.meetingPlatform || MeetingPlatform.GOOGLE;
    let link = `https://coffeeconnex.ai/meeting/${Math.random().toString(36).substr(2, 9)}`;
    
    const updated = { 
      ...selectedReq, 
      scheduledTime: new Date(time).getTime(), 
      schedulingStatus: SchedulingStatus.CONFIRMED,
      meetingLink: link,
      updatedAt: Date.now() 
    };

    await dbService.saveRequest(updated);
    const finalReq = dbService.addMessageToRequest(updated.id, { 
        senderId: user.id, 
        text: `Confirmed! ☕️ I've locked in ${new Date(time).toLocaleString()}. Join here: ${link}` 
    });

    if (finalReq) {
        setSelectedReq(finalReq);
        setRequests(dbService.getRequestsForUser(user.id, user.role));
    }
  };

  const handleGetAiCoaching = async (goal: string, type: string) => {
    if (!selectedReq) return;
    setIsAiLoading(true);
    setCurrentPromptType(type);
    try {
      const studentProfile = dbService.getStudentProfile(selectedReq.studentId);
      const profProfile = dbService.getProfessionalProfile(selectedReq.professionalId);
      const lastMessages = selectedReq.messages.slice(-3).map(m => m.text).join(" | ");
      const result = await aiService.coachDraftMessage(
        studentProfile, 
        profProfile, 
        `${goal}. Context: ${lastMessages || "Starting the conversation"}`
      );
      setAiCoachingResult(result);
    } catch (e) {
      console.error(e);
    } finally {
      setIsAiLoading(false);
    }
  };

  // LinkedIn-style Sidebar Filtering
  const sidebarRequests = useMemo(() => {
    let filtered = isPro 
      ? requests.filter(r => r.status === RequestStatus.ACCEPTED || r.status === RequestStatus.COMPLETED)
      : requests;

    if (searchQuery.trim()) {
      filtered = filtered.filter(req => {
        const partner = dbService.getUsers().find(u => u.id === (isPro ? req.studentId : req.professionalId));
        return partner?.fullName.toLowerCase().includes(searchQuery.toLowerCase());
      });
    }

    return filtered.sort((a,b) => b.updatedAt - a.updatedAt);
  }, [requests, isPro, searchQuery]);

  const partnerUser = selectedReq ? dbService.getUsers().find(u => u.id === (isPro ? selectedReq.studentId : selectedReq.professionalId)) : null;
  const partnerProfile = selectedReq ? (isPro ? dbService.getStudentProfile(selectedReq.studentId) : dbService.getProfessionalProfile(selectedReq.professionalId)) : null;

  return (
    <div className="flex h-screen bg-slate-50/50 dark:bg-slate-950/50 overflow-hidden font-sans text-slate-900 dark:text-slate-100 transition-colors duration-500">
        {/* Left Sidebar */}
        <div className="w-96 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col shrink-0 overflow-hidden hidden md:flex shadow-sm">
           <div className="p-6 border-b border-slate-200 dark:border-slate-800 space-y-4">
              <div className="flex justify-between items-center">
                 <h2 className="text-xl font-black text-blue-950 dark:text-white tracking-tight">Messaging</h2>
                 <button className="text-slate-400 hover:text-blue-600"><i className="fas fa-ellipsis-h"></i></button>
              </div>
              <div className="relative">
                 <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-xs"></i>
                 <input 
                    type="text" 
                    placeholder="Search messages" 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-slate-100 dark:bg-slate-800 border-none rounded-lg text-sm font-medium outline-none focus:ring-2 ring-blue-500/20 dark:ring-blue-400/20 transition-all dark:text-white"
                 />
              </div>
           </div>
           
           <div className="flex-1 overflow-y-auto scrollbar-hide">
              {sidebarRequests.length === 0 ? (
                 <div className="p-12 text-center text-slate-300 dark:text-slate-700">
                    <i className="fas fa-comment-dots text-4xl mb-4 opacity-20"></i>
                    <p className="text-xs font-bold uppercase tracking-widest">Inbox Zero</p>
                 </div>
              ) : sidebarRequests.map(req => {
                const chatPartner = dbService.getUsers().find(u => u.id === (isPro ? req.studentId : req.professionalId));
                const isSelected = selectedReq?.id === req.id;
                const isPending = req.status === RequestStatus.PENDING;
                const isDeclined = req.status === RequestStatus.DECLINED;
                const isApex = (req.synergyScore || 0) >= 95;
                const lastMsg = req.messages[req.messages.length - 1];
                const date = new Date(req.updatedAt);
                
                return (
                  <button 
                    key={req.id} 
                    onClick={() => { setSelectedReq(req); setAiCoachingResult(null); }}
                    className={`w-full p-4 text-left transition-all border-l-4 relative group flex gap-4 ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-600' : 'bg-white dark:bg-slate-900 border-transparent hover:bg-slate-50 dark:hover:bg-slate-800 border-b border-slate-50 dark:border-slate-800'}`}
                  >
                    <div className="relative shrink-0">
                       <div className={`w-14 h-14 rounded-full flex items-center justify-center font-black text-lg shadow-sm border ${isSelected ? 'bg-blue-600 text-white border-blue-500' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 border-slate-200 dark:border-slate-700'}`}>
                          {chatPartner?.fullName[0]}
                       </div>
                       {isApex && (
                         <div className="absolute -top-1 -right-1 bg-amber-400 text-white w-5 h-5 rounded-full flex items-center justify-center text-[8px] border-2 border-white dark:border-slate-900 shadow-sm" title="Synergy Apex">
                            <i className="fas fa-gem"></i>
                         </div>
                       )}
                    </div>
                    
                    <div className="flex-1 min-w-0 pr-2">
                       <div className="flex items-center justify-between gap-1 mb-0.5">
                          <p className={`font-bold text-[14px] truncate ${isSelected ? 'text-blue-950 dark:text-white' : 'text-slate-900 dark:text-slate-300'}`}>{chatPartner?.fullName}</p>
                          <span className="text-[10px] text-slate-400 font-medium whitespace-nowrap">
                             {date.toLocaleDateString() === new Date().toLocaleDateString() ? date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : date.toLocaleDateString(undefined, {month:'short', day:'numeric'})}
                          </span>
                       </div>
                       <p className={`text-xs truncate ${isSelected ? 'text-blue-800 dark:text-blue-400' : isPending ? 'text-amber-600 font-bold' : isDeclined ? 'text-red-400' : 'text-slate-500 dark:text-slate-500'}`}>
                          {isPending ? 'Action Required: Inbound Protocol' : 
                           isDeclined ? 'Handshake Cycle Terminated' : 
                           lastMsg ? lastMsg.text : 'No messages yet.'}
                       </p>
                    </div>
                  </button>
                );
              })}
           </div>
        </div>

        {/* Main Messenger Hub */}
        <div className="flex-1 flex flex-col relative bg-white dark:bg-slate-950">
          {selectedReq ? (
            <>
              {/* Header */}
              <header className="h-20 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-8 flex items-center justify-between z-20">
                <div className="flex items-center gap-4">
                   <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 rounded-full flex items-center justify-center font-black text-lg border border-slate-200 dark:border-slate-700">
                     {partnerUser?.fullName[0]}
                   </div>
                   <div>
                      <h3 className="text-base font-black text-slate-900 dark:text-white leading-tight">{partnerUser?.fullName}</h3>
                      <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                        {isPro ? (partnerProfile as any)?.school : (partnerProfile as any)?.title + ' @ ' + (partnerProfile as any)?.company}
                      </p>
                   </div>
                </div>
                
                <div className="flex items-center gap-3">
                    <button className="w-10 h-10 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-blue-600 transition-colors"><i className="fas fa-video"></i></button>
                    <button className="w-10 h-10 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-blue-600 transition-colors"><i className="fas fa-info-circle"></i></button>
                </div>
              </header>

              {/* Feed */}
              <div ref={scrollRef} className="flex-1 p-8 overflow-y-auto space-y-8 scroll-smooth">
                {/* Protocol Start Block */}
                <div className="flex flex-col items-center py-10 border-b border-slate-50 dark:border-slate-900 mb-6">
                   <div className="w-20 h-20 bg-slate-50 dark:bg-slate-900 rounded-full flex items-center justify-center text-3xl text-slate-200 dark:text-slate-800 mb-4 border border-slate-100 dark:border-slate-800">
                      {partnerUser?.fullName[0]}
                   </div>
                   <h4 className="text-lg font-black text-slate-900 dark:text-white">{partnerUser?.fullName}</h4>
                   <p className="text-xs text-slate-500 dark:text-slate-400 font-medium text-center max-w-xs mt-2">
                      Handshake protocol initiated on {new Date(selectedReq.createdAt).toLocaleDateString()}. Synergy Level: {selectedReq.synergyScore}%
                   </p>
                </div>

                {/* Messages */}
                {selectedReq.messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.senderId === user.id ? 'justify-end' : 'justify-start'}`}>
                    <div className="max-w-[70%] group">
                       <div className={`p-4 rounded-2xl text-[14px] font-medium leading-relaxed shadow-sm ${
                         msg.senderId === user.id 
                         ? 'bg-blue-600 text-white rounded-tr-none' 
                         : 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded-tl-none'
                       }`}>
                          {msg.text}
                       </div>
                       <p className={`text-[9px] font-bold text-slate-400 dark:text-slate-600 mt-2 ${msg.senderId === user.id ? 'text-right' : 'text-left'}`}>
                          {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                       </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <footer className="p-6 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800">
                <div className="max-w-4xl mx-auto">
                    <div className="flex gap-3 items-end">
                      <div className="flex-1">
                         <textarea 
                          value={msgInput} 
                          onChange={e => setMsgInput(e.target.value)} 
                          onKeyPress={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSendMsg())}
                          placeholder="Write a message..."
                          className="w-full bg-slate-100 dark:bg-slate-800 border-none rounded-xl px-5 py-4 font-medium outline-none h-14 min-h-[56px] max-h-32 focus:ring-2 ring-blue-500/10 dark:ring-blue-400/20 transition-all dark:text-white resize-none shadow-inner text-sm" 
                        />
                      </div>
                      <button 
                        onClick={handleSendMsg} 
                        disabled={!msgInput.trim()}
                        className="w-14 h-14 bg-blue-600 text-white rounded-xl flex items-center justify-center hover:bg-blue-700 transition shadow-lg disabled:opacity-20 active:scale-95"
                      >
                        <i className="fas fa-paper-plane"></i>
                      </button>
                    </div>
                </div>
              </footer>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-10 bg-slate-50/10 dark:bg-slate-900/10">
               <div className="w-24 h-24 bg-white dark:bg-slate-900 rounded-full shadow-xl flex items-center justify-center text-slate-100 dark:text-slate-800 text-5xl mb-8 border-4 border-slate-50 dark:border-slate-800">
                  <i className="fas fa-envelope-open"></i>
               </div>
               <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight mb-2">Focused Messaging.</h3>
               <p className="text-slate-400 dark:text-slate-500 font-medium max-w-xs">Select a synchronization cycle from the sidebar to manage conversations and logistics.</p>
            </div>
          )}
        </div>
    </div>
  );
};

export default ChatView;